import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { foraTheme, foraDarkTheme } from './config/foraTheme';
import { useColorMode } from './lib/colorMode';
import Layout from './components/Layout';
import RequireAuth from './components/RequireAuth';
import Login from './pages/Login';
import ProductRegistration from './pages/ProductRegistration';
import ReservationManagement from './pages/ReservationManagement';
import SettlementSystem from './pages/SettlementSystem';
import DetailPageSettingsRouter from './pages/DetailPageSettingsRouter';
import PriceStockRouter from './pages/PriceStockRouter';
import QuickProductWizard from './pages/QuickProductWizard';
import OrderListPage from './pages/OrderListPage';
import QuoteDetailPage from './pages/QuoteDetailPage';
import ReservationDetail from './pages/ReservationDetail';
import OtherCompanyReservationDetail from './pages/OtherCompanyReservationDetail';
import QuoteManagement from './pages/QuoteManagement';
import HotelProducts from './pages/HotelProducts';
import SingleProducts from './pages/SingleProducts';
import PartnerManagement from './pages/PartnerManagement';
import BankAccountManagement from './pages/BankAccountManagement';
import SharedInventoryManagement from './pages/SharedInventoryManagement';
import ProductCodeManagement from './pages/ProductCodeManagement';
import ClientManagement from './pages/ClientManagement';
import PromotionManagement from './pages/PromotionManagement';
import GeneralDetailPageSettings from './pages/GeneralDetailPageSettings';
import GeneralPriceStockSettings from './pages/GeneralPriceStockSettings';
import GeneralOrderListPage from './pages/GeneralOrderListPage';
import PGTransactionHistory from './pages/PGTransactionHistory';
import OutgoingMailManagement from './pages/OutgoingMailManagement';
import ScheduleManagement from './pages/ScheduleManagement';
import ClarityAnalyticsPage from './pages/ClarityAnalyticsPage';
import QuoteProducts from './pages/QuoteProducts';
import GroupSettlement from './pages/GroupSettlement';
import QuoteComparePage from './pages/QuoteComparePage';
import OpenEstimateRequests from './pages/OpenEstimateRequests';
import ConsultManagementLayout from './pages/ConsultManagementLayout';
import ManualCenter from './pages/ManualCenter';
import ManualViewer from './pages/ManualViewer';
import ManualRouteShell from './components/ManualRouteShell';

function App() {
  const [maintenanceInfo, setMaintenanceInfo] = useState(null);
  const colorMode = useColorMode();

  useEffect(() => {
    const onMaintenance = (e) => {
      const detail = e && e.detail ? e.detail : null;
      setMaintenanceInfo({
        status: detail?.status ?? null,
        message: detail?.message ?? null,
        ts: detail?.ts ?? Date.now(),
      });
    };

    const onOnline = () => {
      setMaintenanceInfo(null);
    };

    window.addEventListener('app:maintenance', onMaintenance);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('app:maintenance', onMaintenance);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  return (
    <ConfigProvider theme={colorMode === 'dark' ? foraDarkTheme : foraTheme}>
      {maintenanceInfo && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 px-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <div className="text-lg font-bold text-slate-900">서비스 업그레이드 중입니다</div>
            <div className="mt-2 text-sm text-slate-600">
              현재 서버가 배포/업데이트 중이어서 접속이 불안정할 수 있습니다. 잠시 후 다시 시도해 주세요.
            </div>
            <div className="mt-3 text-xs text-slate-500">
              {maintenanceInfo.status ? `HTTP ${maintenanceInfo.status}` : '네트워크 연결 오류'}
            </div>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                className="flex-1 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
                onClick={() => window.location.reload()}
              >
                다시 시도
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
                onClick={() => setMaintenanceInfo(null)}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      <Routes>
        <Route path="/login" element={<Login />} />

        {/* 매뉴얼은 로그인 없이도 볼 수 있다. 로그인 상태면 앱 레이아웃 안에서 열린다. */}
        <Route path="/manuals" element={<ManualRouteShell />}>
          <Route index element={<ManualCenter />} />
          <Route path=":manualId" element={<ManualViewer />} />
        </Route>

        <Route path="/" element={<RequireAuth><Layout /></RequireAuth>}>
          <Route index element={<Navigate to="/products/tour" replace />} />
          <Route path="settlements" element={<SettlementSystem />} />
          <Route path="products" element={<Navigate to="/products/tour" replace />} />
          <Route path="products/tour" element={<ProductRegistration />} />
          <Route path="products/quote" element={<QuoteProducts />} />
          <Route path="products/hotel" element={<HotelProducts />} />
          <Route path="products/single" element={<SingleProducts />} />
          <Route path="quick-wizard" element={<QuickProductWizard />} />
          <Route path="reservations" element={<ReservationManagement />} />
          <Route path="reservation-detail/:orderNo" element={<ReservationDetail />} />
          <Route path="other-company-reservation-detail/:orderNo" element={<OtherCompanyReservationDetail />} />
          <Route path="schedule" element={<ScheduleManagement />} />
          <Route path="quotes/compare" element={<QuoteComparePage />} />
          <Route path="quotes" element={<ConsultManagementLayout />}>
            <Route index element={<QuoteManagement />} />
            <Route path="open" element={<OpenEstimateRequests />} />
          </Route>
          <Route path="quote-detail/:productCode" element={<QuoteDetailPage />} />
          <Route path="detail-settings/:productId" element={<DetailPageSettingsRouter />} />
          <Route path="price-stock/:productId" element={<PriceStockRouter />} />
          <Route path="orders/:productCode" element={<OrderListPage />} />
          <Route path="settings/partners" element={<PartnerManagement />} />
          <Route path="settings/accounts" element={<BankAccountManagement />} />
          <Route path="settings/pg-transactions" element={<PGTransactionHistory />} />
          <Route path="settings/shared-inventory" element={<SharedInventoryManagement />} />
          <Route path="settings/product-codes" element={<ProductCodeManagement />} />
          <Route path="settings/outgoing-mail" element={<OutgoingMailManagement />} />
          <Route path="settings/clients" element={<ClientManagement />} />
          <Route path="settings/promotions" element={<PromotionManagement />} />
          <Route path="general/detail-settings/:productId" element={<GeneralDetailPageSettings />} />
          <Route path="general/price-stock/:productId" element={<GeneralPriceStockSettings />} />
          <Route path="general/orders/:productCode" element={<GeneralOrderListPage />} />
          <Route path="analytics/clarity" element={<ClarityAnalyticsPage />} />
          <Route path="settings/group-settlement" element={<GroupSettlement />} />
        </Route>
      </Routes>
    </ConfigProvider>
  );
}

export default App;


